import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiTrash2 } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const SHIPMENT_TYPE_MAP = {
  truck: { prefix: '/truck', label: 'Truck' },
  ocean_import: { prefix: '/ocean-import', label: 'OI' },
  ocean_export: { prefix: '/ocean-export', label: 'OE' },
  air_import: { prefix: '/air-import', label: 'AI' },
  misc: { prefix: '/misc', label: 'Misc' },
};

const InvoiceCostList = ({ listType }) => {
  // listType: 'AR' = Invoice List, 'AP' = Cost List, undefined = all
  const navigate = useNavigate();
  const [charges, setCharges] = useState([]);
  const [summary, setSummary] = useState({ ar_amount: 0, ar_paid: 0, ar_balance: 0, ap_amount: 0, ap_paid: 0, ap_balance: 0, profit: 0 });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(listType || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paymentAmounts, setPaymentAmounts] = useState({}); // { chargeId: amount }

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/invoice-cost');
      setCharges(res.data.charges || []);
      setSummary(res.data.summary || { ar_amount: 0, ar_paid: 0, ar_balance: 0, ap_amount: 0, ap_paid: 0, ap_balance: 0, profit: 0 });
    } catch (error) {
      toast.error('Failed to load invoices/costs');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  let filtered = charges;
  // When listType is set, pre-filter by AR or AP
  // D/C charges: show in A/R if revenue > 0, in A/P if only cost > 0 (no revenue)
  if (listType === 'AR') {
    filtered = filtered.filter(c => c.charge_type === 'AR' || (c.charge_type === 'DC' && Number(c.revenue) > 0));
  } else if (listType === 'AP') {
    filtered = filtered.filter(c => c.charge_type === 'AP' || (c.charge_type === 'DC' && !Number(c.revenue) && Number(c.cost) > 0));
  }
  if (typeFilter && !listType) filtered = filtered.filter(c => c.charge_type === typeFilter);
  if (statusFilter) filtered = filtered.filter(c => c.status === statusFilter);
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(c =>
      (c.party || '').toLowerCase().includes(q) ||
      (c.file_no || '').toLowerCase().includes(q) ||
      (c.invoice_no || '').toLowerCase().includes(q) ||
      (c.mbl_no || '').toLowerCase().includes(q) ||
      (c.hbl_no || '').toLowerCase().includes(q) ||
      (c.bill_to || '').toLowerCase().includes(q)
    );
  }

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/invoice-cost/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AR_AP_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('AR/AP Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleDelete = async (e, row) => {
    e.stopPropagation();
    const label = row.invoice_no || `${row.charge_type} #${row.id}`;
    if (!window.confirm(`Delete "${label}"? This will also delete all line items. This cannot be undone.`)) return;
    try {
      await api.delete(`/charges/${row.id}`);
      toast.success(`${label} deleted`);
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleRowClick = (row) => {
    // Navigate to the shipment's detail page (Accounting tab)
    const typeInfo = SHIPMENT_TYPE_MAP[row.shipment_type] || SHIPMENT_TYPE_MAP.truck;
    navigate(`${typeInfo.prefix}/${row.shipment_id}`);
  };

  const toggleSelect = (id, balance) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setPaymentAmounts(p => { const n = { ...p }; delete n[id]; return n; });
      } else {
        next.add(id);
        setPaymentAmounts(p => ({ ...p, [id]: Number(balance) || 0 }));
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
      setPaymentAmounts({});
    } else {
      const all = new Set();
      const amounts = {};
      filtered.forEach(c => { all.add(c.id); amounts[c.id] = Number(c.balance) || 0; });
      setSelectedIds(all);
      setPaymentAmounts(amounts);
    }
  };

  const selectedTotal = Object.values(paymentAmounts).reduce((s, v) => s + v, 0);
  const selectedInvoiceAmt = filtered.filter(c => selectedIds.has(c.id)).reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const selectedBalanceAmt = filtered.filter(c => selectedIds.has(c.id)).reduce((s, c) => s + (Number(c.balance) || 0), 0);

  const handleCreatePayment = () => {
    if (selectedIds.size === 0) { toast.info('Please select invoices first'); return; }
    const selected = filtered.filter(c => selectedIds.has(c.id));
    const firstSelected = selected[0];
    const mode = firstSelected?.charge_type === 'AR' ? 'receive' : 'make';
    // Pass selected invoices via route state
    const invoices = selected.map(c => ({
      charge_id: c.id,
      invoice_no: c.invoice_no || '',
      post_date: c.post_date || null,
      invoice_date: c.invoice_date || null,
      due_date: c.due_date || null,
      customer: c.party || '',
      mbl_no: c.mbl_no || '',
      hbl_no: c.hbl_no || '',
      file_no: c.file_no || '',
      invoice_amount: c.amount || 0,
      balance_amount: c.balance || 0,
      payment_amount: paymentAmounts[c.id] || c.balance || 0,
      charge_type: c.charge_type || '',
      description: c.description || '',
    }));
    const party = firstSelected?.party || '';
    navigate(`/payment/new?mode=${mode}`, { state: { invoices, party } });
  };

  const columns = [
    { key: 'party', label: 'Party', sortable: true },
    {
      key: 'file_no', label: 'File No.', sortable: true,
      render: (v, row) => {
        const typeInfo = SHIPMENT_TYPE_MAP[row.shipment_type] || SHIPMENT_TYPE_MAP.truck;
        return (
          <Link
            to={`${typeInfo.prefix}/${row.shipment_id}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: '#2563eb', fontSize: '12px', textDecoration: 'none' }}
          >
            {v || '-'}
          </Link>
        );
      },
    },
    {
      key: 'invoice_no', label: 'Invoice No.', sortable: true,
      render: (v, row) => (
        <Link
          to={`/charge/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', fontWeight: 500, textDecoration: 'none' }}
        >
          {v || 'view'}
        </Link>
      ),
    },
    {
      key: 'charge_type', label: 'Type', sortable: true,
      render: (v) => {
        const colors = { AR: '#059669', AP: '#dc2626', DC: '#7c3aed' };
        const labels = { AR: 'A/R', AP: 'A/P', DC: 'Debit' };
        return (
          <span style={{ color: colors[v] || '#64748b', fontWeight: 600, fontSize: '11px' }}>
            {labels[v] || v}
          </span>
        );
      },
    },
    {
      key: 'post_date', label: 'Invoice Post Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      key: 'publication_status', label: 'Publication Status', sortable: true,
      render: (v) => {
        const status = v || 'Unpublished';
        const color = status === 'Published' ? '#059669' : '#94a3b8';
        return <span style={{ color, fontWeight: 500, fontSize: '11px' }}>{status}</span>;
      },
    },
    {
      key: 'invoice_date', label: 'Invoice Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      key: 'due_date', label: 'Due Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      key: 'amount', label: 'Amount (A.C.)', sortable: true,
      render: (v) => <span style={{ textAlign: 'right', display: 'block' }}>{formatCurrency(v)}</span>,
    },
    {
      key: 'paid_amount', label: 'Paid Amount (A.C.)', sortable: true,
      render: (v) => <span style={{ textAlign: 'right', display: 'block' }}>{formatCurrency(v)}</span>,
    },
    {
      key: 'balance', label: 'Balance (A.C.)', sortable: true,
      render: (v) => <span style={{ textAlign: 'right', display: 'block', fontWeight: 600 }}>{formatCurrency(v)}</span>,
    },
    {
      key: '_select', label: '', sortable: false, width: '30px',
      headerRender: () => (
        <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
      ),
      render: (v, row) => (
        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={(e) => { e.stopPropagation(); toggleSelect(row.id, row.balance); }} style={{ cursor: 'pointer' }} />
      ),
    },
    {
      key: '_payment', label: 'Payment', sortable: false,
      render: (v, row) => selectedIds.has(row.id) ? (
        <input
          type="number" step="0.01"
          value={paymentAmounts[row.id] || 0}
          onChange={(e) => { e.stopPropagation(); setPaymentAmounts(p => ({ ...p, [row.id]: Number(e.target.value) || 0 })); }}
          onClick={(e) => e.stopPropagation()}
          style={{ width: '100px', textAlign: 'right', padding: '3px 6px', border: '1px solid #3b82f6', borderRadius: '4px', fontSize: '12px', background: '#eff6ff' }}
        />
      ) : null,
    },
    {
      key: 'issued_by', label: 'Issued by', sortable: true,
    },
    {
      key: 'issue_date', label: 'Issue Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    { key: 'mbl_no', label: 'MB/L No.', sortable: true },
    { key: 'hbl_no', label: 'HB/L No.', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
    { key: 'op', label: 'OP', sortable: true },
    {
      key: '_delete', label: '', sortable: false,
      render: (v, row) => (
        <button
          onClick={(e) => handleDelete(e, row)}
          title="Delete"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', padding: '4px', borderRadius: '4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
          onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
        >
          <FiTrash2 size={14} />
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="card">
        {/* Header with totals */}
        <div className="card-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <h2 className="card-title" style={{ margin: 0 }}>{listType === 'AR' ? 'INVOICE LIST (A/R)' : listType === 'AP' ? 'COST LIST (A/P)' : 'INVOICE / COST LIST'}</h2>
              <button
                onClick={handleExport}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  backgroundColor: '#059669',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Export AR/AP Report
              </button>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                <strong>A/R: <span style={{ color: '#2563eb' }}>{formatCurrency(summary.ar_amount)}</span></strong>
                {' (Paid: '}<span style={{ color: '#059669' }}>{formatCurrency(summary.ar_paid)}</span>
                {' | Balance: '}<span style={{ color: '#dc2626' }}>{formatCurrency(summary.ar_balance)}</span>{') '}
                {' | '}
                <strong>A/P: <span style={{ color: '#dc2626' }}>{formatCurrency(summary.ap_amount)}</span></strong>
                {' (Paid: '}<span style={{ color: '#059669' }}>{formatCurrency(summary.ap_paid)}</span>
                {' | Balance: '}<span style={{ color: '#dc2626' }}>{formatCurrency(summary.ap_balance)}</span>{') '}
                {' | '}
                <strong>Profit: <span style={{ color: summary.profit >= 0 ? '#059669' : '#dc2626' }}>{formatCurrency(summary.profit)}</span></strong>
              </span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-bar">
          {!listType && (
            <div className="filter-group">
              <label className="filter-label">Type:</label>
              <select className="filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All</option>
                <option value="AR">A/R (Invoice)</option>
                <option value="AP">A/P (Cost)</option>
                <option value="DC">Debit/Credit</option>
              </select>
            </div>
          )}
          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Draft">Draft</option>
              <option value="Open">Open</option>
              <option value="Paid">Paid</option>
              <option value="Void">Void</option>
            </select>
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Search by party, file no, invoice no, MB/L, HB/L..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filtered}
          loading={loading}
          onRowClick={handleRowClick}
          pageSize={20}
        />

        {/* Selection totals bar */}
        {selectedIds.size > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '12px 16px', borderTop: '2px solid #e2e8f0', background: '#f8fafc', gap: '24px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>{selectedIds.size} selected</span>
            <div>
              <span style={{ fontSize: '12px', fontWeight: 600 }}>Invoice AMT: </span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#2563eb' }}>{formatCurrency(selectedInvoiceAmt)}</span>
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: 600 }}>Balance AMT: </span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(selectedBalanceAmt)}</span>
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: 600 }}>Payment: </span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#059669' }}>{formatCurrency(selectedTotal)}</span>
            </div>
            <button onClick={handleCreatePayment} style={{ padding: '6px 14px', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
              Create Payment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceCostList;
